/**
 * Turn whatever an owner types into a canonical "City, ST".
 *
 * Owners type zips, bare city names, "city, state", mixed case — the listing
 * should always display a city and a state. Two lookups cover it:
 *
 *   zip   -> api.zippopotam.us (free, no key, returns city + state + coords)
 *   text  -> Nominatim (already used elsewhere in this app for geocoding)
 *
 * When more than one real city matches, the caller is expected to ask the owner
 * which one they mean rather than guessing — "Springfield" alone matches eight
 * states.
 */

const ZIP_PATTERN = /^\s*(\d{5})(?:-\d{4})?\s*$/;
const LOOKUP_TIMEOUT_MS = 8000;

const STATE_ABBREVIATIONS = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID', illinois: 'IL',
  indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA',
  maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI',
  minnesota: 'MN', mississippi: 'MS', missouri: 'MO', montana: 'MT',
  nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
  'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR',
  pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT',
  vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY', 'puerto rico': 'PR',
};

const VALID_ABBREVIATIONS = new Set(Object.values(STATE_ABBREVIATIONS));

export function isZipInput(text) {
  return ZIP_PATTERN.test(String(text || ''));
}

export function extractZip(text) {
  const match = ZIP_PATTERN.exec(String(text || ''));
  return match ? match[1] : null;
}

/** Accepts "Utah", "UT", or Nominatim's "US-UT" and returns "UT". */
export function toStateAbbreviation(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const isoMatch = /^US-([A-Za-z]{2})$/.exec(raw);
  if (isoMatch) return isoMatch[1].toUpperCase();

  if (raw.length === 2 && VALID_ABBREVIATIONS.has(raw.toUpperCase())) {
    return raw.toUpperCase();
  }

  return STATE_ABBREVIATIONS[raw.toLowerCase()] || '';
}

function titleCaseCity(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) =>
      word
        .split('-')
        .map((part) =>
          part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part
        )
        .join('-')
    )
    .join(' ');
}

export function formatCityState(candidate) {
  if (!candidate) return '';
  const city = titleCaseCity(candidate.city);
  const state = toStateAbbreviation(candidate.state);
  if (!city) return '';
  return state ? `${city}, ${state}` : city;
}

/**
 * Splits "Cedar City, UT" into its parts so a typed state can narrow results,
 * and a typed city can be matched against what the geocoder returns.
 */
export function parseTypedLocation(text) {
  const raw = String(text || '').trim().replace(/\s+/g, ' ');
  if (!raw) return { cityText: '', stateText: '' };

  const segments = raw.split(',').map((segment) => segment.trim()).filter(Boolean);

  if (segments.length >= 2) {
    const tail = segments[segments.length - 1];
    const state = toStateAbbreviation(tail.replace(/\s+\d{5}(?:-\d{4})?$/, ''));
    if (state) {
      return { cityText: segments.slice(0, -1).join(', '), stateText: state };
    }
    return { cityText: segments.join(', '), stateText: '' };
  }

  // No comma: a trailing state word or abbreviation is still worth honouring,
  // so "Enoch UT" narrows to Utah rather than searching the whole country.
  const words = segments[0].split(' ');
  if (words.length > 1) {
    const state = toStateAbbreviation(words[words.length - 1]);
    if (state) {
      return { cityText: words.slice(0, -1).join(' '), stateText: state };
    }
  }

  return { cityText: segments[0], stateText: '' };
}

async function fetchJson(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function dedupeCandidates(candidates) {
  const seen = new Map();
  candidates.forEach((candidate) => {
    const label = formatCityState(candidate);
    if (!label) return;
    if (!seen.has(label)) {
      seen.set(label, { ...candidate, city: titleCaseCity(candidate.city), state: toStateAbbreviation(candidate.state), label });
    }
  });
  return Array.from(seen.values());
}

async function lookupZip(zip) {
  const data = await fetchJson(`https://api.zippopotam.us/us/${zip}`);
  const places = data && Array.isArray(data.places) ? data.places : [];
  return dedupeCandidates(
    places.map((place) => ({
      city: place['place name'],
      state: place['state abbreviation'] || place.state,
      latitude: Number(place.latitude),
      longitude: Number(place.longitude),
      zip,
    }))
  );
}

/**
 * Nominatim happily returns loosely-related places — searching "Cedar City"
 * also suggests Cedar Falls and Jefferson City. Only keep results whose city
 * actually matches what was typed.
 */
function cityMatchesQuery(city, cityText) {
  const a = String(city || '').toLowerCase();
  const b = String(cityText || '').toLowerCase();
  if (!a || !b) return false;
  return a === b || a.startsWith(b) || b.startsWith(a);
}

async function lookupText(text) {
  const { cityText, stateText } = parseTypedLocation(text);
  if (!cityText) return [];

  const params = new URLSearchParams({
    q: stateText ? `${cityText}, ${stateText}` : cityText,
    countrycodes: 'us',
    format: 'json',
    limit: '10',
    addressdetails: '1',
  });

  const results = await fetchJson(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`
  );
  if (!Array.isArray(results)) return [];

  const mapped = results
    .map((result) => {
      const address = result.address || {};
      const city =
        address.city || address.town || address.village || address.hamlet ||
        address.municipality || address.county;
      const state = address['ISO3166-2-lvl4'] || address.state;
      return {
        city,
        state,
        latitude: Number(result.lat),
        longitude: Number(result.lon),
        kind: result.type,
      };
    })
    .filter((candidate) => candidate.city && toStateAbbreviation(candidate.state))
    .filter((candidate) => cityMatchesQuery(candidate.city, cityText))
    .filter((candidate) => !stateText || toStateAbbreviation(candidate.state) === stateText);

  // An exact name match beats a partial one, so "Cedar City" does not rank
  // "Cedar City Junction" above the real thing.
  const exact = mapped.filter(
    (candidate) => String(candidate.city).toLowerCase() === cityText.toLowerCase()
  );

  return dedupeCandidates(exact.length ? exact : mapped);
}

/**
 * Resolves typed input to one or more "City, ST" candidates.
 *
 * status:
 *   'resolved'   exactly one city — safe to use directly
 *   'ambiguous'  several real cities — ask the owner which one
 *   'not_found'  nothing matched the input
 *   'unavailable' the lookup service could not be reached
 */
export async function resolveLocationInput(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { status: 'not_found', candidates: [] };

  const zip = extractZip(trimmed);
  const candidates = zip ? await lookupZip(zip) : await lookupText(trimmed);

  if (!candidates.length) {
    return {
      status: 'not_found',
      candidates: [],
      zip: zip || null,
      query: trimmed,
    };
  }

  return {
    status: candidates.length === 1 ? 'resolved' : 'ambiguous',
    candidates,
    zip: zip || null,
    query: trimmed,
  };
}
